import { ofType } from 'redux-observable';
import { mergeMap, withLatestFrom } from 'rxjs/operators';

import { ERROR_CONFLICT } from '@perpay-web/constants/httpStatusCodes';
import { analyticsIdentifyUser } from '@perpay-web/fintech/actions/analytics/userInfo';
import {
    replaceAuthTokens,
    partnerOnboardingRedirectExistingUser,
} from '@perpay-web/fintech/actions/authentication';
import {
    paychexOnboardingSignupSuccess,
    paychexOnboardingSignupError,
} from '@perpay-web/fintech/actions/entities/paychexOnboarding';
import { analyticsSignupSuccess } from '@perpay-web/fintech/actions/ui/signup';
import { PAYCHEX } from '@perpay-web/fintech/constants/partnerOnboardingCodes';
import { PerpayProduct } from '@perpay-web/fintech/constants/products';
import { SIGNUP_FLOW_PAYROLL_PROVIDER_ONBOARDING } from '@perpay-web/fintech/constants/signupFlowTypes';
import { paychexOnboardingSignupDataModuleState$ } from '@perpay-web/fintech/hooks/dataModule/usePaychexOnboardingSignupContext';
import {
    partnerOnboardingExistingUserModalSetExistingUserFirstName,
    partnerOnboardingExistingUserModalSetIsModalOpen,
    partnerOnboardingExistingUserModalSetMatchMethod,
} from '@perpay-web/fintech/hooks/usePartnerOnboardingExistingUserModalContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getUserInfo } from '@perpay-web/fintech/selectors/entities/userInfo';
import { authentication } from '@perpay-web/fintech/settings/singletons';
import { installSegmentMiddleware } from '@perpay-web/fintech/utils/analyticsUtils';
import { replace } from '@perpay-web/services/router';
import { firstItemOf } from '@perpay-web/utils/arrayUtils';
import { getAnalyticsIdentity } from '@perpay-web/utils/tokenUtils';

export const paychexOnboardingSignup = (action$, state$) =>
    action$.pipe(
        ofType(paychexOnboardingSignupSuccess().type),
        withLatestFrom(state$),
        mergeMap(([results, state]) => {
            const { tokens: unusedTokens, ...rest } = results;

            installSegmentMiddleware();

            const decodedTokens = authentication.getDecodedTokens();
            const identityAction = getAnalyticsIdentity(
                decodedTokens,
                getUserInfo(state),
            );
            const uuid = authentication.getUserUuid();

            replace(paths.paychexSSORedirect.path);
            return [
                analyticsIdentifyUser(identityAction),
                replaceAuthTokens(decodedTokens),
                analyticsSignupSuccess({
                    ...rest,
                    uuid,
                    signupFlow: SIGNUP_FLOW_PAYROLL_PROVIDER_ONBOARDING,
                    partner: PAYCHEX,
                    product: PerpayProduct.MARKETPLACE,
                }),
            ];
        }),
    );

export const paychexOnboardingSignupErrorHandler = (action$) =>
    action$.pipe(
        ofType(paychexOnboardingSignupError().type),
        withLatestFrom(paychexOnboardingSignupDataModuleState$),
        mergeMap(([, paychexDataModuleState]) => {
            const { rawErrorData } = paychexDataModuleState;
            const returnActions = [];

            if (rawErrorData && rawErrorData.status === ERROR_CONFLICT) {
                const {
                    message,
                    name: firstName,
                    method,
                    mfaRequired,
                } = rawErrorData.response;
                const isMfaRequired = mfaRequired
                    ? firstItemOf(mfaRequired)
                    : false;

                returnActions.push(partnerOnboardingRedirectExistingUser());

                if (
                    (method && method === 'phone') ||
                    (firstItemOf(message) &&
                        firstItemOf(message).includes('Phone')) ||
                    isMfaRequired
                ) {
                    replace(paths.login.path);
                    return returnActions;
                }
                returnActions.push(
                    partnerOnboardingExistingUserModalSetIsModalOpen(true),
                );
                if (firstName) {
                    returnActions.push(
                        partnerOnboardingExistingUserModalSetExistingUserFirstName(
                            firstName,
                        ),
                    );
                }
                if (method) {
                    returnActions.push(
                        partnerOnboardingExistingUserModalSetMatchMethod(
                            method,
                        ),
                    );
                }
            }

            return returnActions;
        }),
    );
